"use client";

import { Contact } from "@/@types";
import {
  Disclosure,
  DisclosureButton,
  DisclosurePanel,
} from "@headlessui/react";
import Image from "next/image";
import Link from "next/link";
import { ROUTES } from "@/config/routes";
import { Branches } from "@/@types/Branches";
import { IconSprite } from "../common";
import Dropdown from "../common/Dropdown";
import { useEffect } from "react";

const navigation = [
  {
    name: "NOSOTROS",
    href: ROUTES.aboutUs.root,
    submenu: {
      name: "TRABAJA CON NOSOTROS",
      href: ROUTES.workWithUs.root,
    },
  },
  { name: "NOTICIAS", href: ROUTES.news.root },
  { name: "LITERATURA", href: ROUTES.literature.root },
  { name: "SUCURSALES", href: ROUTES.branches.root },
];

type Props = {
  contact?: Contact;
  menus: { name: string; slug: string }[];
  branches?: Branches[];
};

function LockBodyScroll({ active }: { active: boolean }) {
  useEffect(() => {
    document.body.style.overflow = active ? "hidden" : "";

    return () => {
      document.body.style.overflow = "";
    };
  }, [active]);

  return null;
}

export default function TopNav({ contact, menus, branches }: Props) {
  const phone = contact?.phone || "800 22 MATCO";
  const email = contact?.email || "info@matco.com.mx";

  return (
    <Disclosure as="nav" className="bg-gray01 px-0 lg:px-8">
      {({ open }) => (
        <>
        <LockBodyScroll active={open} />
          <div className="flex flex-wrap uppercase px-10">
            {/* Desktop menu */}
            <div className="hidden lg:flex lg:space-x-8 items-center text-gray [@media(min-width:1024px)_and_(max-width:1099px)]:text-[12px] [@media(min-width:1100px)_and_(max-width:1200px)]:text-[13px] [@media(min-width:1201px)_and_(max-width:1325px)]:text-[15px]">
              {navigation.map((item) => (
                <div key={item.name}>
                  {(branches?.length ?? 0) < 1 &&
                    item.href === ROUTES.branches.root ? (
                    <></>
                  ) : item.submenu ? (
                    <Dropdown
                      menu={{
                        name: item.name,
                        slug: ROUTES.aboutUs.root.replace(/^\//, ""),
                        inventories: [
                          { title: "Acerca de nosotros", link: item.href },
                          {
                            title: item.submenu.name,
                            link: item.submenu.href,
                          },
                        ],
                      }}
                      hasSubmenu
                      position="left"
                    />
                  ) : (
                    <Link className="hover:text-black" href={item.href}>
                      {item.name}
                    </Link>
                  )}
                </div>
              ))}
            </div>

            <div className="flex flex-1"></div>

            {/* Contact info */}
            <div className="hidden lg:ml-6 lg:flex lg:items-center gap-8 px-4 py-2 font-bold border-b-2 border-grey [@media(min-width:1024px)_and_(max-width:1099px)]:text-[12px] [@media(min-width:1100px)_and_(max-width:1200px)]:text-[13px] [@media(min-width:1201px)_and_(max-width:1325px)]:text-[15px]">
              
              <a href={`tel:+52${phone.replace(/\s+/g, "")}`}>
                <span>{phone}</span>
              </a>
              
              <a href={`mailto:${email}`}>
                <span>{email}</span>
              </a>

            </div>

            {/* Contact us button */}
            <button className="hidden lg:flex items-center bg-matco px-2 uppercase font-bold hover:bg-yellow02 [@media(min-width:1024px)_and_(max-width:1099px)]:text-[12px] [@media(min-width:1100px)_and_(max-width:1200px)]:text-[13px] [@media(min-width:1201px)_and_(max-width:1325px)]:text-[15px]">
              <a href={ROUTES.contact.root}>contáctanos</a>
            </button>
          </div>

          {/* Mobile menu header */}
          <div
            className={`flex items-center lg:hidden px-6 py-4 ${open ? "justify-between" : "justify-start"
              }`}
          >
            {!open && (
              <DisclosureButton className="inline-flex justify-center text-black hover:text-dark02 focus:outline-none">
                <span className="sr-only">Abrir menú principal</span>
                <IconSprite id="ic_hamburger_menu" className="w-6 h-6" />
              </DisclosureButton>
            )}

            <Link prefetch={false} href="/" className={`${open ? "" : "ml-3"}`}>
              <Image
                src="/logo.svg"
                alt="Logo oficial de Matco - Soluciones y Maquinaria Pesada"
                width={135}
                height={32}
                className="hover:cursor-pointer"
                priority={true}
              />
            </Link>

            <div className="flex items-center space-x-3 ml-auto">
              <a
                href={`tel:+52${phone.replace(/\s+/g, "")}`}
                className="hover:cursor-pointer"
              >
                <div>
                  <IconSprite id="ic_tel" className="w-9 h-9" />
                </div>
              </a>

              <a href={`mailto:${email}`} className="hover:cursor-pointer">
                <div>
                  <IconSprite id="ic_mail" className="w-9 h-9" />
                </div>
              </a>

              <Link href="/contacto">
                <div>
                  <IconSprite
                    id="ic_chat"
                    className="w-9 h-9 text-transparent"
                  />
                </div>
              </Link>
            </div>
          </div>

          {/* Mobile menu */}
          <DisclosurePanel className="lg:hidden">
            {/* Overlay */}
            <DisclosureButton
              className="fixed inset-0 bg-black/70 z-60"
              aria-label="Cerrar menú"
            />

            {/* Drawer */}
            <div className="fixed inset-y-0 left-0 w-[344px] z-70">
              <div className="h-full flex flex-col bg-gray01">
                {/* Header */}
                <div className="flex items-center justify-between px-6 py-4 border-b border-gray01 bg-light00">
                  <Link
                    prefetch={false}
                    href="/"
                    onClick={() =>
                      open &&
                      (
                        document.querySelector(
                          '[aria-label="Cerrar menú"]'
                        ) as HTMLButtonElement
                      )?.click()
                    }
                  >
                    <Image
                      src="/logo.svg"
                      alt="Logo oficial de Matco - Soluciones y Maquinaria Pesada"
                      width={135}
                      height={32}
                      className="hover:cursor-pointer"
                      priority={true}
                    />
                  </Link>
                  <DisclosureButton className="p-2 hover:opacity-75 transition-opacity">
                    <IconSprite id="ic_close" className="w-4 h-4 text-gray02" />
                  </DisclosureButton>
                </div>

                {/* Main menu items */}
                <div className="flex-1 overflow-y-auto space-y-2">
                  <div className="bg-light01">
                    {menus.map((menu, index) => (
                      <Link
                        href={`/${menu.slug}`}
                        className="flex items-center justify-between w-full px-6 py-2 text-dark01 text-lg border-b border-gray01 uppercase hover:bg-gray01 transition-colors"
                        key={index}
                        onClick={() =>
                          (
                            document.querySelector(
                              '[aria-label="Cerrar menú"]'
                            ) as HTMLButtonElement
                          )?.click()
                        }
                      >
                        <span>{menu.name}</span>
                        <IconSprite
                          id="ic_arrow_right"
                          className="w-4 h-4 text-gray02"
                        />
                      </Link>
                    ))}
                  </div>

                  <div className="bg-gray01">
                    {navigation.map((item) => (
                      <div key={item.name}>
                        <Link
                          href={item.href}
                          className="flex items-center justify-between w-full px-6 py-2 text-dark01 text-lg border-b border-gray01 hover:bg-gray02 transition-colors"
                          onClick={() =>
                            (
                              document.querySelector(
                                '[aria-label="Cerrar menú"]'
                              ) as HTMLButtonElement
                            )?.click()
                          }
                        >
                          <span>{item.name}</span>
                        </Link>
                        {item.submenu && (
                          <Link
                            href={item.submenu.href}
                            className="flex items-center justify-between w-full px-6 py-2 text-dark01 text-lg border-b border-gray01 hover:bg-gray02 transition-colors"
                            key={item.submenu.name}
                            onClick={() =>
                              (
                                document.querySelector(
                                  '[aria-label="Cerrar menú"]'
                                ) as HTMLButtonElement
                              )?.click()
                            }
                          >
                            <span>{item.submenu.name}</span>
                          </Link>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </DisclosurePanel>
        </>
      )}
    </Disclosure>
  );
}
